import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default icon paths (Webpack mangles them)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const ACCENT = () => getComputedStyle(document.documentElement).getPropertyValue("--coral").trim() || "#E07A5F";

// Coral pin built inline as DivIcon
function coralIcon() {
  const c = ACCENT();
  return L.divIcon({
    className: "ovamx-pin",
    iconSize: [30, 40],
    iconAnchor: [15, 38],
    popupAnchor: [0, -34],
    html: `
      <svg width="30" height="40" viewBox="0 0 30 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 0C6.7 0 0 6.7 0 15c0 11 15 25 15 25s15-14 15-25C30 6.7 23.3 0 15 0z" fill="${c}"/>
        <circle cx="15" cy="14" r="6" fill="white"/>
      </svg>
    `,
  });
}

function FitBounds({ points, fallbackCenter }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) {
      if (fallbackCenter) map.setView(fallbackCenter, 4);
      return;
    }
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 13);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [points, map, fallbackCenter]);
  return null;
}

/**
 * Generic MapView for listings/restaurants with lat/lng.
 *
 * Props:
 *   items: array of { id, lat, lng, title, ...renderable_extras }
 *   center: [lat, lng] fallback when no items have GPS
 *   renderPopup: (item) => JSX
 *   onMarkerClick: (item) => void
 *   height: CSS height (default '70vh')
 *   testid: optional data-testid for the wrapping div
 */
export default function MapView({
  items = [],
  center = [19.4326, -99.1332], // Default Mexico City
  renderPopup,
  onMarkerClick,
  height = "70vh",
  testid = "map-view",
}) {
  const points = useMemo(
    () => items.filter((it) => typeof it.lat === "number" && typeof it.lng === "number"),
    [items]
  );
  const icon = useRef(null);
  if (!icon.current) icon.current = coralIcon();

  return (
    <div data-testid={testid} className="w-full rounded-2xl overflow-hidden border hairline relative" style={{ height }}>
      <MapContainer center={center} zoom={4} scrollWheelZoom={true} style={{ width: "100%", height: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} fallbackCenter={center} />
        {points.map((it) => (
          <Marker
            key={it.id}
            position={[it.lat, it.lng]}
            icon={icon.current}
            eventHandlers={onMarkerClick ? { click: () => onMarkerClick(it) } : undefined}
          >
            {renderPopup && (
              <Popup>
                {renderPopup(it)}
              </Popup>
            )}
          </Marker>
        ))}
      </MapContainer>
      {points.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <p className="bg-cream/90 backdrop-blur-sm border hairline rounded-full px-4 py-2 text-xs text-ink-mid">
            Ninguno de los resultados tiene coordenadas GPS para mostrar en el mapa
          </p>
        </div>
      )}
    </div>
  );
}
